import { NgModule } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ServiceBootstrapComponent } from './components/service-bootstrap/service-bootstrap.component';
import { GoMaterialModule } from './go-material.module';
import { LicenseManagementModule } from '@go-modules/components/license-management';
import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { GoMonitorTabEventModule } from './directives/go-monitor-tab-event';
import { RequiredFieldsNoteModule } from './directives/required-fields-note';
import { NgxZeroStateModule } from '@go-modules/components/zero-state/zero-state.module';
import { FeedbackCreatorModule } from './components/feedback-session/feedback-creator';
import { GoToastModule } from '@go-modules/components/go-toast/go-toast.module';
import { NgxRichTextEditorModule } from './components/rich-text-editor/rich-text-editor.module';
import { LicenseSeatsManagementModule } from './components/license-seats-management';
import { WelcomeDialogModule } from './components/dialogs/welcome-dialog/welcome-dialog.module';
import { NgxActivityViewModule } from '@go-modules/components/activity-view/activity-view.module';
import { GoSelectModule } from './directives/go-select/go-select.module';
import { OutsideClickModule } from './directives/outside-click';
import { LicenseRenewalSnackBarModule } from '@go-modules/components/snack-bars/license-renewal';
import { LoginModule } from './components/auth/login';
import { UserSettingsModule } from '@go-modules/components/user-settings';
import { TranscriptViewerModule } from './components/feedback-session/transcript-viewer';
import { SessionAnalyticsModule } from '@go-modules/components/feedback-session/session-analytics';
import { SpeedGraderModule } from '@go-modules/components/feedback-session/speed-grader/speed-grader.module';
import { CopyToClipboardDirectiveModule } from '@go-modules/directives/go-clipboard/copy-to-clipboard.module';
import { DisabledInteractiveDirectiveModule } from '@go-modules/directives/disabled-interactive/disabled-interactive.module';
import { UiTimerDirectiveModule } from '@go-modules/directives/ui-timer/ui-timer.module';
import { DropUserDialogModule } from './components/dialogs/drop-user-dialog';
import { GoSidepanelModule } from './services/go-sidepanel/go-sidepanel.module';
import { MessageDialogModule } from './components/dialogs/message-dialog/message-dialog.module';
import { ConfirmDialogModule } from './components/dialogs/confirm-dialog/confirm-dialog.module';
import { NgxArchiveViewModule } from '@go-modules/components/archive/archive-view/archive-view.module';
import { DashboardModule } from '@go-modules/components/dashboard/dashboard.module';
import { NgxFolderViewModule } from '@go-modules/components/folder-view/folder-view.module';
import { AIMarkersDialogModule } from './components/dialogs/ai-markers-dialog/ai-markers-dialog.module';
import { WelcomeTourDialogModule } from '@go-modules/components/dialogs/welcome-tour-dialog/welcome-tour-dialog.module';
import { NgxSessionViewModule } from './components/feedback-session/session-view/session-view.module';
import { TimeDisplayModule } from '@go-modules/components/time-display/time-display.module';
import { RateLimitInterceptor } from '@go-modules/services/interceptors/rate-limit-interceptor/rate-limit-interceptor.service';
import { RateLimitDialogModule } from '@go-modules/components/dialogs/rate-limit-dialog/rate-limit-dialog.module';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { TermsOfUseDialogModule } from '@go-modules/components/dialogs/terms-of-use-dialog/terms-of-use-dialog.module';
import { ActivitySourceDialogModule } from '@go-modules/components/dialogs/activity-source-dialog/activity-source-dialog.module';
import { VideoSceneModule } from '@go-modules/components/video-scene/video-scene.module';
import { AspectRatioContainerModule } from '@go-modules/components/aspect-ratio-container/aspect-ratio-container.module';
import { VideoDetailsDialogModule } from '@go-modules/components/dialogs/video-details-dialog/video-details-dialog.module';
import { CompareProductsDialogModule } from './components/dialogs/compare-products-dialog/compare-products-dialog.module';
import {
	LicenseUpgradeSnackBarModule
} from '@go-modules/components/snack-bars/license-upgrade/license-upgrade-snack-bar.module';
import { RubricFilterDropdownModule } from './components/feedback-session/feedback-filters/rubric-filter/rubric-filter-dropdown.module';
import { PaymentPanelModule } from '@go-modules/components/payment-panel/payment-panel.module';
import { NgxNoGroupsModule } from '@go-modules/components/no-groups/no-groups.module';
import { InterceptorsModule } from './services/interceptors';
import { CopyPreviousAssignmentDialogModule } from '@go-modules/components/dialogs/copy-previous-assignment-dialog/copy-previous-assignment-dialog.module';
import { StartTestDialogModule } from './components/dialogs/start-test-dialog/start-test-dialog.module';
import { NgxToolbarModule } from '@go-modules/components/toolbar/toolbar.module';
import { NgxToolbarButtonModule } from '@go-modules/components/toolbar/toolbar-button/toolbar-button.module';
import { ActivityInfoModule } from './components/activity-info/activity-info.module';
import { IosUploadDialogModule } from './components/dialogs/ios-upload-dialog/ios-upload-dialog.module';
import { AccountLockoutDialogModule } from './components/dialogs/account-lockout-dialog';
import { CountryPickerModule } from './components/country-picker/country-picker.module';
import { MessageRememberingDialogModule } from './components/dialogs/message-remembering/message-remembering.dialog.module';
import { InstructionsPreviewDialogModule } from './components/dialogs/instructions-preview/instructions-preview-dialog.module';
import { InviteUsersDialogModule } from './components/dialogs/invite-users-dialog/invite-users-dialog.module';
import { NgxCommentCaptionsModule } from '@go-modules/components/comment-captions/comment-captions.module';
import { CommentViewerModule } from './components/feedback-session/comment-viewer/comment-viewer.module';
import { FeedbackPanelTabsModule } from './components/feedback-session/feedback-panel-tabs/feedback-panel-tabs.module';
import { PanelModule } from './components/panel/panel.module';
import { TimeRemainingCountdownModule } from './components/video-scene/time-remaining-countdown/time-remaining-countdown.module';
import { ActivityReportComponent } from '@go-modules/components/activity-report/activity-report.component';
import { GoBannerModule } from './components/go-banner/go-banner.module';
import { SessionAttachmentsPopoverModule } from '@go-modules/components/feedback-session/session-attachments-popover/session-attachments-popover.module';

// Only import modules here if you really need to (i.e.
// - the module exports a directive
// - the component is opened from a service)
// - the component is downgraded and used in ng1 (if so leave a comment about when to remove)
@NgModule({
	imports: [
		CommonModule,
		TranslateModule,
		FormsModule,
		ReactiveFormsModule,
		GoMaterialModule,
		InterceptorsModule,
		LicenseManagementModule, /* TODO - remove when licenseManagementRouteView STAB-802 and routingConfig */
		LicenseSeatsManagementModule, /* TODO - remove when licenseSeatsManagement STAB-637 and routingConfig  */
		FeedbackCreatorModule, /* TODO - remove when feedbackSession ngx STAB-680 */
		A11yModule,
		CopyToClipboardDirectiveModule,
		DisabledInteractiveDirectiveModule,
		UiTimerDirectiveModule,
		GoMonitorTabEventModule,
		RequiredFieldsNoteModule,
		NgxZeroStateModule, /* TODO - remove when rubricFeedbackViewer STAB-748, coursePaymentWall STAB-615 */
		GoToastModule,
		NgxRichTextEditorModule, /* TODO - remove when activityInstructionsEditor STAB-947 */
		GoSelectModule,
		WelcomeDialogModule, /* TODO - remove when dashboardRootView STAB-634 */
		NgxActivityViewModule, /* TODO - remove when routesConfig ngx STAB-1182 */
		OutsideClickModule,
		LicenseRenewalSnackBarModule,
		LicenseUpgradeSnackBarModule,
		LoginModule, /* TODO - remove when loginView STAB-630 */
		UserSettingsModule, /* TODO - remove when routesConfig ngx STAB-1182 */
		TranscriptViewerModule, /* TODO - remove when feedbackSession ngx STAB-680 */
		SessionAnalyticsModule, /* TODO - remove when feedbackSession ngx STAB-680 */
		SpeedGraderModule, /* TODO - remove when feedbackSession ngx STAB-680 */
		DropUserDialogModule, /* TODO - remove when coursePaymentWall STAB-615 */
		GoSidepanelModule,
		MessageDialogModule,
		ConfirmDialogModule,
		NgxArchiveViewModule, /* TODO - remove when routesConfig ngx STAB-1182 */
		DashboardModule,
		NgxFolderViewModule, /* TODO - remove when routesConfig ngx STAB-1182 */
		NgxSessionViewModule, /* TODO - remove when routesConfig ngx STAB-1182 */
		AIMarkersDialogModule, /* TODO - remove when activityEditor STAB-645 and feedbackDisplay STAB-846 */
		WelcomeTourDialogModule,
		RateLimitDialogModule,
		TermsOfUseDialogModule, /* TODO - remove when dashboardRootView STAB-634 */
		ActivitySourceDialogModule, /* TODO - remove when activityEditorWizardView STAB-807 */
		TimeDisplayModule, /* TODO - remove when videoScene STAB-779 */
		VideoSceneModule, /* TODO - remove when UMCRecordController STAB-774 and videoScene STAB-779 */
		AspectRatioContainerModule,
		RubricFilterDropdownModule, /* TODO - remove when rubricFeedbackViewer STAB-748 */
		VideoDetailsDialogModule, /* TODO - remove when feedbackSessionHeader STAB-686 and SessionEditorPanel STAB-916 */
		CompareProductsDialogModule, /* TODO - remove when licensePaymentWizardView STAB-810 licenseDetailsForm STAB-738 and LazyPaymentLoader STAB-984 */
		PaymentPanelModule, /* TODO - remove from here when paymentPanel STAB-908 */
		NgxNoGroupsModule, /* TODO - remove from here when routesConfig ngx STAB-1182 */
		CopyPreviousAssignmentDialogModule, /* TODO - remove from here when activityEditorWizardView ngx STAB-807  */
		StartTestDialogModule, /* TODO - remove from here when sessionCreationWizardStartTestStep STAB-1126 */
		NgxToolbarModule,
		NgxToolbarButtonModule,
		ActivityInfoModule, /* TODO - remove when feedbackDisplay STAB-846 */
		IosUploadDialogModule,
		AccountLockoutDialogModule, /* TODO - remove when loginView STAB-630 */
		CountryPickerModule,
		MessageRememberingDialogModule, /* TODO - remove from here when videoScene STAB-779 */
		InstructionsPreviewDialogModule, /* TODO - remove from here when activityEditor STAB-645 and activityInstructionsEditor STAB-947 */
		InviteUsersDialogModule,
		NgxCommentCaptionsModule,
		ActivityReportComponent,
		TimeRemainingCountdownModule,
		CommentViewerModule,
		FeedbackPanelTabsModule,
		PanelModule,
		GoBannerModule,
		SessionAttachmentsPopoverModule
	],
	declarations: [
		ServiceBootstrapComponent
	],
	exports: [
		ServiceBootstrapComponent
	],
	providers: [
		{ provide: 'Window', useValue: window },
		{ provide: HTTP_INTERCEPTORS, useClass: RateLimitInterceptor, multi: true }
	]
})
export class GoModulesModule {}
